import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { motion } from 'framer-motion';
import { ArrowDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [showAll, setShowAll] = useState(false); // state untuk toggle see more

  useEffect(() => {
    const csvUrl = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRc_9sEf35kYOkAabz2oXltF_T7yHIZbIXAypjVBdyOrz-9kOZvyO8EzwOs2D_Cv9LzksBjzm7nCr4i/pub?gid=0&single=true&output=csv';

    Papa.parse(csvUrl, {
      download: true,
      header: true,
      complete: (results) => {
        const items = results.data.map(row => ({
          title: row.title,
          description: row.description,
          image: row.image,
          tech: row.tech.split(',').map(t => t.trim()),
          size: row.size
        }));
        setPortfolioItems(items);
      },
      error: (err) => {
        console.error('Error parsing CSV:', err);
      }
    });
  }, []);

  // Batasi jumlah item yang ditampilkan
  const displayedItems = showAll ? portfolioItems : portfolioItems.slice(0, 3);

  return (
    <section id="portfolio" className="py-24 md:py-32 px-6 md:px-12 relative bg-slate-950/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-blue-500 font-mono mb-4">
            Our Work
          </p>
          <h2 className="text-4xl md:text-5xl font-outfit font-semibold tracking-tight">
            Featured Projects
          </h2>
        </motion.div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {displayedItems.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl bg-slate-900/50 border border-slate-800/50 hover:border-blue-500/20 transition-all duration-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.1)] ${
                item.size === 'large' ? 'md:col-span-2' : ''
              }`}
            >
              {/* Image */}
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl md:text-3xl font-outfit font-medium mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 font-inter mb-4 leading-relaxed">
                  {item.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {item.tech.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 text-xs font-mono bg-blue-600/10 text-blue-400 rounded-full border border-blue-500/20"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <button className="inline-flex items-center text-blue-400 hover:text-blue-300 font-inter font-medium transition-colors">
                  View Details
                  <ExternalLink className="ml-2" size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* See More Button */}
        {portfolioItems.length > 3 && (
          <div className="text-center mt-10">
            <Button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="bg-transparent border border-blue-500 text-blue-500 py-3 px-8 rounded-lg font-inter font-medium transition-all duration-200 hover:bg-blue-500/10 hover:text-blue-400 hover:shadow-[0_0_20px_rgba(59,130,246,0.2)] flex items-center justify-center mx-auto"
            >
              {showAll ? 'Show Less' : (
                <>
                  See More
                  <ArrowDown className="ml-2" size={18} />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;
